import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * One curated layout choice, presented in the merged shell top bar's
 * workspace-preset picker (see `app/incident-context-bar.tsx`). Supplied by
 * whichever `FlexWorkspaceView` mount is currently active, so the shell bar
 * never needs to know about `WorkspacePresetChoice`/`WorkspaceModelStore` —
 * and `icon` arrives pre-rendered so this shared context stays free of any
 * icon-registry import (the shell bar must not become a new `ShellIcon`
 * importer; that registry is deprecated in favour of `OperationalIcon`).
 */
export interface WorkspaceLayoutBarPresetChoice {
  readonly id: string;
  readonly icon: ReactNode;
  readonly label: string;
  readonly description?: string | undefined;
  readonly roleDefault?: boolean | undefined;
  readonly active: boolean;
}

export interface WorkspaceLayoutActions {
  readonly reset: () => void;
  readonly save: () => Promise<void>;
  /** Active workspace preset's display name, shown in the merged shell top bar. */
  readonly barLabel: string;
  /** Present only when the role has more than one curated layout to switch between. */
  readonly presetChoices?: readonly WorkspaceLayoutBarPresetChoice[] | undefined;
  readonly onSelectPreset?: ((choiceId: string) => void) | undefined;
}

interface WorkspaceLayoutActionsContextValue {
  readonly actions: WorkspaceLayoutActions | null;
  readonly register: (actions: WorkspaceLayoutActions) => () => void;
}

const unregisteredWorkspaceLayoutActions: WorkspaceLayoutActionsContextValue = {
  actions: null,
  register: () => () => undefined,
};

const WorkspaceLayoutActionsContext =
  createContext<WorkspaceLayoutActionsContextValue>(
    unregisteredWorkspaceLayoutActions,
  );

export function WorkspaceLayoutActionsProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const [actions, setActions] = useState<WorkspaceLayoutActions | null>(null);
  const register = useCallback((nextActions: WorkspaceLayoutActions) => {
    setActions(nextActions);
    return () => {
      setActions((current) => current === nextActions ? null : current);
    };
  }, []);
  const value = useMemo(
    () => ({ actions, register }),
    [actions, register],
  );

  return (
    <WorkspaceLayoutActionsContext.Provider value={value}>
      {children}
    </WorkspaceLayoutActionsContext.Provider>
  );
}

export function useWorkspaceLayoutActions(): WorkspaceLayoutActionsContextValue {
  return useContext(WorkspaceLayoutActionsContext);
}
